import React, { useState, useEffect } from 'react';
import { useProfile } from '@/context';
import ApiService from '@/services/api';
import Swal from 'sweetalert2';
import dayjs from 'dayjs';
import { NumericFormat } from 'react-number-format';
import { formatRupiah } from '@/helpers/utils';
import { useNavigate, useSearchParams } from 'react-router-dom';

const ManualJournal = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const journalId = searchParams.get('id');
  const isEditMode = !!journalId;

  const { profile } = useProfile();
  const cooperativeId = profile?.cooperative?.id;

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [formData, setFormData] = useState({
    transaction_date: dayjs().format('YYYY-MM-DD'),
    description: '',
    details: [
      { account_id: '', description: '', debit_amount: 0, credit_amount: 0 },
      { account_id: '', description: '', debit_amount: 0, credit_amount: 0 }
    ]
  });

  useEffect(() => {
    if (cooperativeId) {
      fetchAllAccounts();
      if (isEditMode && journalId) {
        fetchJournalData(journalId);
      }
    }
  }, [cooperativeId, isEditMode, journalId]);

  const fetchAllAccounts = async () => {
    try {
      const response = await ApiService.getCOA(cooperativeId, 1, 999);
      setAccounts(response?.data || []);
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Failed to load chart of accounts'
      });
    }
  };

  const fetchJournalData = async (id) => {
    setLoadingData(true);
    try {
      const response = await ApiService.getJournalEntryById(id);
      const journalData = response?.data;

      if (journalData) {
        setFormData({
          transaction_date: dayjs(journalData.transaction_date).format('YYYY-MM-DD'),
          description: journalData.description || '',
          details: journalData.details.map(detail => ({
            account_id: detail.account_id || '',
            description: detail.description || '',
            debit_amount: detail.debit_amount || 0,
            credit_amount: detail.credit_amount || 0
          }))
        });
      }
    } catch (error) {
      console.error('Failed to fetch journal data:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Failed to load journal entry data'
      }).then(() => {
        navigate('/admin/report/general-ledger');
      });
    } finally {
      setLoadingData(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDetailChange = (index, field, value) => {
    const newDetails = [...formData.details];
    newDetails[index][field] = value;
    setFormData(prev => ({
      ...prev,
      details: newDetails
    }));
  };

  const addDetailLine = () => {
    setFormData(prev => ({
      ...prev,
      details: [
        ...prev.details,
        { account_id: '', description: '', debit_amount: 0, credit_amount: 0 }
      ]
    }));
  };

  const removeDetailLine = (index) => {
    if (formData.details.length <= 2) {
      Swal.fire({
        icon: 'warning',
        title: 'Warning!',
        text: 'Journal entry must have at least 2 detail lines'
      });
      return;
    }

    const newDetails = formData.details.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      details: newDetails
    }));
  };

  const calculateTotals = () => {
    const totalDebit = formData.details.reduce((sum, detail) => sum + parseFloat(detail.debit_amount || 0), 0);
    const totalCredit = formData.details.reduce((sum, detail) => sum + parseFloat(detail.credit_amount || 0), 0);
    return { totalDebit, totalCredit };
  };

  const validateForm = () => {
    const { totalDebit, totalCredit } = calculateTotals();

    if (!formData.transaction_date) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error!',
        text: 'Transaction date is required'
      });
      return false;
    }

    if (!formData.description.trim()) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error!',
        text: 'Description is required'
      });
      return false;
    }

    if (formData.details.length < 2) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error!',
        text: 'Journal entry must have at least 2 detail lines'
      });
      return false;
    }

    // Check each detail line
    for (let i = 0; i < formData.details.length; i++) {
      const detail = formData.details[i];
      
      if (!detail.account_id) {
        Swal.fire({
          icon: 'error',
          title: 'Validation Error!',
          text: `Account is required for line ${i + 1}`
        });
        return false;
      }

      if (!detail.description.trim()) {
        Swal.fire({
          icon: 'error',
          title: 'Validation Error!',
          text: `Description is required for line ${i + 1}`
        });
        return false;
      }

      const debit = parseFloat(detail.debit_amount || 0);
      const credit = parseFloat(detail.credit_amount || 0);

      if (debit === 0 && credit === 0) {
        Swal.fire({
          icon: 'error',
          title: 'Validation Error!',
          text: `Line ${i + 1} must have either debit or credit amount`
        });
        return false;
      }

      if (debit > 0 && credit > 0) {
        Swal.fire({
          icon: 'error',
          title: 'Validation Error!',
          text: `Line ${i + 1} cannot have both debit and credit amounts`
        });
        return false;
      }
    }

    // Check balanced entry
    if (Math.abs(totalDebit - totalCredit) > 0.01) {
      Swal.fire({
        icon: 'error',
        title: 'Unbalanced Entry!',
        text: `Total Debit (${formatRupiah(totalDebit)}) must equal Total Credit (${formatRupiah(totalCredit)})`
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Check cooperative ID
    if (!cooperativeId) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Cooperative ID not found. Please refresh the page or login again.'
      });
      return;
    }

    setLoading(true);

    try {
      // Debug: Check token and cooperative_id
      const token = localStorage.getItem('accessToken');
      console.log('Debug - Token exists:', !!token);
      console.log('Debug - Cooperative ID:', cooperativeId);

      // Convert date to ISO 8601 datetime format (YYYY-MM-DDTHH:mm:ssZ)
      const transactionDateTime = dayjs(formData.transaction_date).format('YYYY-MM-DDTHH:mm:ss') + 'Z';

      const payload = {
        cooperative_id: cooperativeId,
        transaction_date: transactionDateTime,
        description: formData.description,
        auto_post: true, // Auto-post manual journal entries so they appear immediately in reports
        details: formData.details.map(detail => ({
          account_id: parseInt(detail.account_id),
          description: detail.description,
          debit_amount: parseFloat(detail.debit_amount || 0),
          credit_amount: parseFloat(detail.credit_amount || 0)
        }))
      };

      console.log('Debug - Payload:', payload);

      if (isEditMode) {
        await ApiService.updateJournalEntry(journalId, payload);
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Journal entry updated successfully'
        }).then(() => {
          navigate('/admin/report/general-ledger');
        });
      } else {
        await ApiService.createJournalEntry(payload);
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Journal entry created successfully'
        });

        // Reset form
        setFormData({
          transaction_date: dayjs().format('YYYY-MM-DD'),
          description: '',
          details: [
            { account_id: '', description: '', debit_amount: 0, credit_amount: 0 },
            { account_id: '', description: '', debit_amount: 0, credit_amount: 0 }
          ]
        });
      }

    } catch (error) {
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} journal entry:`, error);
      console.error('Error details:', {
        message: error.message,
        response: error?.response,
        status: error?.response?.status,
        data: error?.response?.data
      });

      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: error?.message || error?.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} journal entry`
      });
    } finally {
      setLoading(false);
    }
  };

  const { totalDebit, totalCredit } = calculateTotals();
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;

  if (loadingData) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <i className="mgc_loading_line animate-spin text-4xl text-blue-600"></i>
            <p className="mt-4 text-gray-600">Memuat data jurnal...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">
          {isEditMode ? 'Edit Jurnal Manual' : 'Buat Jurnal Manual'}
        </h1>
        <p className="text-gray-600 mt-2">
          {isEditMode
            ? 'Update jurnal transaksi yang sudah ada'
            : 'Input jurnal transaksi secara manual dengan double-entry bookkeeping'}
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="bg-white rounded-lg shadow-md p-6">
          {/* Header Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label htmlFor="transaction_date" className="block text-sm font-medium text-gray-700 mb-2">
                Tanggal Transaksi <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                id="transaction_date"
                name="transaction_date"
                value={formData.transaction_date}
                onChange={handleFormChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                Deskripsi Jurnal <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="description"
                name="description"
                value={formData.description}
                onChange={handleFormChange}
                placeholder="Contoh: Pembayaran Listrik Bulan Januari"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          {/* Detail Lines Section */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Detail Jurnal</h3>
              <button
                type="button"
                onClick={addDetailLine}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
              >
                <i className="mgc_add_line"></i>
                Tambah Baris
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border">No</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border">Akun <span className="text-red-500">*</span></th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border">Keterangan <span className="text-red-500">*</span></th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700 border">Debit (Rp)</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700 border">Kredit (Rp)</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 border">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.details.map((detail, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 border text-center text-sm">{index + 1}</td>
                      <td className="px-4 py-3 border">
                        <select
                          value={detail.account_id}
                          onChange={(e) => handleDetailChange(index, 'account_id', e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        >
                          <option value="">Pilih Akun</option>
                          {accounts.map(account => (
                            <option key={account.id} value={account.id}>
                              {account.account_code} - {account.account_name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-3 border">
                        <input
                          type="text"
                          value={detail.description}
                          onChange={(e) => handleDetailChange(index, 'description', e.target.value)}
                          placeholder="Keterangan detail"
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </td>
                      <td className="px-4 py-3 border">
                        <NumericFormat
                          value={detail.debit_amount}
                          onValueChange={(values) => handleDetailChange(index, 'debit_amount', values.value)}
                          thousandSeparator="."
                          decimalSeparator=","
                          prefix="Rp "
                          allowNegative={false}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="0"
                        />
                      </td>
                      <td className="px-4 py-3 border">
                        <NumericFormat
                          value={detail.credit_amount}
                          onValueChange={(values) => handleDetailChange(index, 'credit_amount', values.value)}
                          thousandSeparator="."
                          decimalSeparator=","
                          prefix="Rp "
                          allowNegative={false}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="0"
                        />
                      </td>
                      <td className="px-4 py-3 border text-center">
                        <button
                          type="button"
                          onClick={() => removeDetailLine(index)}
                          className="px-3 py-1 text-red-600 hover:bg-red-50 rounded"
                          disabled={formData.details.length <= 2}
                        >
                          <i className="mgc_delete_line"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-100 font-semibold">
                    <td colSpan="3" className="px-4 py-3 border text-right">Total:</td>
                    <td className={`px-4 py-3 border text-right ${!isBalanced && totalDebit > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                      {formatRupiah(totalDebit)}
                    </td>
                    <td className={`px-4 py-3 border text-right ${!isBalanced && totalCredit > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                      {formatRupiah(totalCredit)}
                    </td>
                    <td className="px-4 py-3 border text-center">
                      {isBalanced && totalDebit > 0 ? (
                        <span className="text-green-600 flex items-center justify-center gap-1">
                          <i className="mgc_check_circle_fill"></i>
                          Balanced
                        </span>
                      ) : totalDebit > 0 || totalCredit > 0 ? (
                        <span className="text-red-600 flex items-center justify-center gap-1">
                          <i className="mgc_close_circle_fill"></i>
                          Unbalanced
                        </span>
                      ) : null}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3">
            {isEditMode ? (
              <button
                type="button"
                onClick={() => navigate('/admin/report/general-ledger')}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Batal
              </button>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setFormData({
                    transaction_date: dayjs().format('YYYY-MM-DD'),
                    description: '',
                    details: [
                      { account_id: '', description: '', debit_amount: 0, credit_amount: 0 },
                      { account_id: '', description: '', debit_amount: 0, credit_amount: 0 }
                    ]
                  });
                }}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Reset
              </button>
            )}
            <button
              type="submit"
              disabled={loading || !isBalanced}
              className={`px-6 py-2 text-white rounded-md flex items-center gap-2 ${
                loading || !isBalanced
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              {loading ? (
                <>
                  <i className="mgc_loading_line animate-spin"></i>
                  Processing...
                </>
              ) : (
                <>
                  <i className="mgc_save_line"></i>
                  {isEditMode ? 'Update Jurnal' : 'Simpan Jurnal'}
                </>
              )}
            </button>
          </div>
        </div>
      </form>

      {/* Help Section */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
          <i className="mgc_information_line"></i>
          Petunjuk Penggunaan
        </h4>
        <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
          <li>Jurnal harus memiliki minimal 2 baris detail (debit dan kredit)</li>
          <li>Total Debit harus sama dengan Total Kredit (balanced entry)</li>
          <li>Setiap baris harus diisi salah satu: Debit ATAU Kredit (tidak boleh keduanya)</li>
          <li>Contoh: Pembayaran Beban Listrik Rp 1.000.000
            <ul className="ml-6 mt-1">
              <li>Baris 1: Beban Listrik (Debit) Rp 1.000.000</li>
              <li>Baris 2: Kas (Kredit) Rp 1.000.000</li>
            </ul>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default ManualJournal;
